import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, inputCls } from "@/components/common";
import { CalendarCheck, Loader2, Copy } from "lucide-react";

const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const plusMonths = (n) => { const d = new Date(); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };
const fmt = (d) => { if (!d) return ""; const [y, m, dd] = d.slice(0, 10).split("-"); return `${dd}/${m}/${y}`; };
const nextMonthFirst = (ym) => { let [y, m] = ym.split("-").map(Number); m++; if (m > 12) { m = 1; y++; } return `${y}-${String(m).padStart(2, "0")}-01`; };
const monthOptions = () => {
  const now = new Date();
  // Orizzonte finale mobile: cresce automaticamente ogni mese (oggi arriva a Dic 2028).
  const end = new Date(now.getFullYear(), now.getMonth() + 27, 1);
  const endY = end.getFullYear(), endM = end.getMonth() + 1;
  const out = []; let y = 2025, m = 12;
  while (y < endY || (y === endY && m <= endM)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
};
const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return `${MONTHS_IT[m - 1]} ${y}`; };

export default function Disponibilita() {
  const [mode, setMode] = useState("periodo"); // 'periodo' | 'mese'
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(plusMonths(12));
  const [month, setMonth] = useState(thisMonth() < "2025-12" ? "2025-12" : thisMonth());
  const [minNights, setMinNights] = useState(4);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });

  const search = useCallback((m = mode, mo = month, f = from, t = to) => {
    const effFrom = m === "mese" ? `${mo}-01` : f;
    const effTo = m === "mese" ? nextMonthFirst(mo) : t;
    if (effTo <= effFrom) { toast.error("La data 'A' deve essere successiva a 'Da'"); return; }
    setLoading(true);
    setRange({ from: effFrom, to: effTo });
    api.get("/availability", { params: { from: effFrom, to: effTo, min_nights: Number(minNights) || 1 } })
      .then((r) => setGroups(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore nel calcolo disponibilità"))
      .finally(() => setLoading(false));
  }, [mode, month, from, to, minNights]);

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const totalFree = groups.reduce((a, g) => a + g.rooms.reduce((x, r) => x + r.free.length, 0), 0);
  const tabCls = (active) => `px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${active ? "bg-sky-700 text-white" : "text-slate-600 hover:bg-slate-100"}`;

  const copyRoom = async (structureName, r) => {
    const title = `${structureName} — ${r.room_name}${r.room_type ? ` (${r.room_type})` : ""}`;
    const lines = r.free.length
      ? r.free.map((f) => `• ${fmt(f.from)} → ${fmt(f.to)} (${f.nights} notti)`).join("\n")
      : `Nessuna disponibilità ≥ ${minNights} notti dal ${fmt(range.from)} al ${fmt(range.to)}.`;
    const text = `${title}\nDisponibilità (min ${minNights} notti):\n${lines}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copiato: ${r.room_name}`);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`Copiato: ${r.room_name}`); }
      catch (_) { toast.error("Copia non riuscita"); }
      document.body.removeChild(ta);
    }
  };

  const copyAll = async () => {
    const blocks = groups.map((g) => {
      const rooms = g.rooms.map((r) => {
        const lines = r.free.length
          ? r.free.map((f) => `  • ${fmt(f.from)} → ${fmt(f.to)} (${f.nights} notti)`).join("\n")
          : `  • nessuna disponibilità ≥ ${minNights} notti`;
        return `${r.room_name}${r.room_type ? ` (${r.room_type})` : ""}:\n${lines}`;
      }).join("\n");
      return `*${g.structure_name}*\n${rooms}`;
    }).join("\n\n");
    const text = `Disponibilità (min ${minNights} notti) dal ${fmt(range.from)} al ${fmt(range.to)}\n\n${blocks}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Elenco completo copiato");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Elenco completo copiato"); }
      catch (_) { toast.error("Copia non riuscita"); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div>
      <PageHeader title="Disponibilità" subtitle="Periodi liberi per camera (solo proprietà abilitate in scheda). Elenco delle finestre disponibili ≥ notti minime.">
        <div className="flex items-center gap-1 p-1 rounded-md border border-slate-300 bg-white">
          <button data-testid="avail-tab-periodo" className={tabCls(mode === "periodo")} onClick={() => { setMode("periodo"); search("periodo"); }}>Periodo</button>
          <button data-testid="avail-tab-mese" className={tabCls(mode === "mese")} onClick={() => { setMode("mese"); search("mese"); }}>Mese</button>
        </div>
        {mode === "periodo" ? (
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-300 bg-white">
            <span className="text-xs text-slate-500">Da</span>
            <input data-testid="avail-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-sm outline-none bg-transparent w-[125px]" />
            <span className="text-xs text-slate-500">a</span>
            <input data-testid="avail-to" type="date" min={from} value={to} onChange={(e) => setTo(e.target.value)} className="text-sm outline-none bg-transparent w-[125px]" />
          </div>
        ) : (
          <select data-testid="avail-month" value={month} onChange={(e) => { setMonth(e.target.value); search("mese", e.target.value); }} className={inputCls + " w-auto"}>
            {monthOptions().map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-slate-300 bg-white">
          <span className="text-xs text-slate-500">Notti min.</span>
          <input data-testid="avail-min-nights" type="number" min={1} value={minNights} onChange={(e) => setMinNights(e.target.value)} className="text-sm outline-none bg-transparent w-[52px] text-right" />
        </div>
        <Btn data-testid="avail-search" onClick={() => search()} disabled={loading}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} Cerca
        </Btn>
      </PageHeader>

      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {loading ? "Calcolo in corso…" : <>Trovate <span className="font-semibold text-slate-800">{totalFree}</span> finestre libere ≥ {minNights} notti dal {fmt(range.from)} al {fmt(range.to)}.</>}
        </div>
        {!loading && groups.length > 0 && (
          <button data-testid="avail-copy-all" onClick={copyAll} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 shrink-0">
            <Copy size={15} /> Copia tutto
          </button>
        )}
      </div>

      {!loading && groups.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md px-4 py-12 text-center text-slate-400">
          Nessuna proprietà abilitata. Spunta "Mostra in Disponibilità" nella scheda della proprietà.
        </div>
      )}

      <div className="space-y-5" data-testid="avail-list">
        {groups.map((g) => (
          <div key={g.structure_id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-800 text-white text-sm font-semibold uppercase tracking-wide">{g.structure_name}</div>
            <div className="divide-y divide-slate-100">
              {g.rooms.map((r) => (
                <div key={r.room_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-2" data-testid={`avail-room-${r.room_id}`}>
                  <div className="sm:w-56 shrink-0 flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-slate-800">{r.room_name}</div>
                      {r.room_type && <div className="text-xs text-slate-400">{r.room_type}</div>}
                    </div>
                    <button data-testid={`avail-copy-${r.room_id}`} onClick={() => copyRoom(g.structure_name, r)} title="Copia le date libere di questa camera" className="p-1.5 text-slate-400 hover:text-sky-600 shrink-0">
                      <Copy size={15} />
                    </button>
                  </div>
                  <div className="flex-1">
                    {r.free.length === 0 ? (
                      <div className="text-sm text-slate-400">nessuna disponibilità ≥ {minNights} notti nel periodo</div>
                    ) : (
                      <ul className="space-y-1">
                        {r.free.map((f, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="tabular">{fmt(f.from)} → {fmt(f.to)}</span>
                            <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">{f.nights} notti</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
